import { useCallback, useState } from 'react';
import { formatNumberWithCommas } from '@rocco/utils/format-number';

/**
 * Keep only what a range input accepts: digits, plus one decimal point and up
 * to `decimalPlaces` fraction digits when decimals are allowed.
 */
export const sanitiseRangeInput = (raw: string, decimalPlaces = 0): string => {
    if (decimalPlaces <= 0) {
        return raw.replace(/[^0-9]/g, '');
    }

    const [whole = '', ...fractions] = raw.replace(/[^0-9.]/g, '').split('.');
    if (fractions.length === 0) {
        return whole;
    }

    return `${whole}.${fractions.join('').slice(0, decimalPlaces)}`;
};

/** Format a display value for a range input, rounded to the decimals it accepts. */
export const formatRangeInputValue = (
    value: number,
    decimalPlaces = 0,
): string => {
    if (decimalPlaces <= 0) {
        return formatNumberWithCommas(value);
    }

    const factor = 10 ** decimalPlaces;
    return formatNumberWithCommas(Math.round(value * factor) / factor);
};

interface RangeInputDraft {
    field: string;
    text: string;
}

/**
 * Holds the raw text of the range input being edited when it accepts
 * decimals. Re-formatting the parsed number on every keystroke would swallow
 * a trailing "." or "0" before the next digit lands, so the field shows what
 * was typed until it loses focus. Whole-number inputs keep no draft and
 * re-format as they are typed.
 */
export const useRangeInputDraft = (decimalPlaces = 0) => {
    const [draft, setDraft] = useState<RangeInputDraft | null>(null);

    /** Parse typed text to a display value, holding the text as the field's draft. */
    const readInput = useCallback(
        (field: string, raw: string): number => {
            const text = sanitiseRangeInput(raw, decimalPlaces);
            if (decimalPlaces > 0) {
                setDraft({ field, text });
            }
            return Number(text) || 0;
        },
        [decimalPlaces],
    );

    /** The text a field shows: its draft while being edited, otherwise the formatted value. */
    const getInputText = (field: string, formatted: string): string =>
        draft?.field === field ? draft.text : formatted;

    const clearDraft = useCallback(() => setDraft(null), []);

    return { readInput, getInputText, clearDraft };
};
